import { Preload, useGLTF } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { AnimationAction, AnimationMixer, Clock, DoubleSide, LoopRepeat, Material, Mesh, MeshBasicMaterial, MeshPhysicalMaterial, MeshStandardMaterial, Object3D, PointLight } from "three";
import { PageType } from "@/types";

import { FloatAnimation } from "./FloatAnimation";
import { WaterScene } from "./Ocean_Marlin/WaterScene";
import { MaterialLibrary } from "@/materials/MaterialLibrary";

const Model3D = forwardRef(({ url, freezeTime = 0, onLoad, onAnimationDone, page }: { url: string; freezeTime: number; onLoad: () => void; onAnimationDone: () => void; page: PageType }, ref) => {
	const { gl, camera, scene: realScene } = useThree();
	const mixer = useRef<AnimationMixer | null>(null);
	const actions = useRef<AnimationAction[]>([]);
	const [isLoaded, setIsLoaded] = useState(false);
	const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);
	const { scene, animations } = useGLTF(process.env.NEXT_PUBLIC_API_ORIGIN + url);

	const [paused, setPaused] = useState<boolean>(true);

	const clock = useMemo(() => new Clock(), []);

	const [masterAnimation, setMasterAnimation] = useState<AnimationAction | null>(null);
	// const [animatedMaterials, setAnimatedMaterials] = useState<Material[]>([]);
	// const [alwaysanimatedMaterials, setAlwaysAnimatedMaterials] = useState<Material[]>([]);

	const animatedMaterials = useRef<Material[]>([]);
	const alwaysanimatedMaterials = useRef<Material[]>([]);

	const [animationTime, setAnimationTime] = useState<number>(0);

	const [loopIteration, setLoopIteration] = useState<number>(0);

	useImperativeHandle(ref, () => ({
		playAnimation() {
			if (!isAnimationPlaying) {
				setLoopIteration(0);
				actions.current.forEach((action) => {
					action.stop();
					action.reset();
					action.setLoop(LoopRepeat, Infinity);
					action.paused = false;
					action.time = freezeTime || 0;
					action.play();
				});
				setIsAnimationPlaying(true);
			}
		},
		pauseAnimation() {
			// setLoopIteration(0);
			// actions.current.forEach((action) => {
			// 	action.paused = true;
			// });
		},
		setTime(time: number) {
			// console.log("set time => ", time, actions.current);
			// actions.current.forEach((action) => {
			// 	action.reset();
			// 	action.paused = false;
			// 	action.play();
			// 	action.time = time || 0;
			// 	// Mixer mit einer minimalen Zeitdifferenz aktualisieren
			// 	if (mixer.current) {
			// 		mixer.current.update(0.001); // Zwingt den Mixer, den Zustand anzuwenden
			// 	}
			// 	action.paused = true;
			// });
		},
		getDuration() {
			let duration = 0;
			actions.current.forEach((action) => {
				const clipDur = action.getClip().duration;
				duration = Math.max(duration, clipDur);
			});
			return duration;
		},
		getTime() {
			let time = 0;
			actions.current.forEach((action) => {
				time = action.time;
			});
			return time;
		},
	}));

	const setTime = useCallback((time: number) => {
		// console.log("set time => ", time, actions.current);
		// actions.current.forEach((action) => {
		// 	action.reset();
		// 	action.paused = false;
		// 	action.play();
		// 	action.time = time || 0;
		// 	// Mixer mit einer minimalen Zeitdifferenz aktualisieren
		// 	if (mixer.current) {
		// 		mixer.current.update(0.001); // Zwingt den Mixer, den Zustand anzuwenden
		// 	}
		// 	action.paused = true;
		// });
	}, []);

	const cleanup = useCallback(() => {
		if (mixer.current) {
			mixer.current.stopAllAction();
			mixer.current.removeEventListener("finished", onAnimationDone);
			mixer.current = null;
		}
		setIsLoaded(false);
		setLoopIteration(0);
	}, [mixer, setIsLoaded, onAnimationDone]);

	useEffect(() => {
		if (animations.length && !mixer.current) {
			scene.traverse((obj) => {
				if (obj.type === "PointLight") {
					(obj as PointLight).intensity /= 500;
				}
			});

			mixer.current = new AnimationMixer(scene);
			// mixer.current.addEventListener("finished", () => {
			// 	// Wenn die Animation einmal durchgelaufen ist
			// 	// spiele sie von vorne ab bis die freezeTime wieder erreicht ist
			// 	if (loopIteration === 0) {
			// 		setLoopIteration(1);
			// 		actions.current.forEach((action) => {
			// 			action.reset();
			// 			action.play();
			// 		});

			// 		console.log("loop iteration 1");
			// 	}
			// });

			actions.current = animations
				.map((clip) => {
					const action = mixer.current?.clipAction(clip);

					// console.log("clip Name => ", clip);
					if (action) {
						setMasterAnimation(action);
					}

					if (action) {
						action.setLoop(LoopRepeat, Infinity);
						action.timeScale = 1;
						action.play();
					}
					return action;
				})
				.filter((action) => action != null);

			setTime(freezeTime);
		}

		if (scene) {
			setIsLoaded(true);
			replaceMaterial(scene);
			onLoad();
		}

		return () => {
			cleanup();
		};
	}, []);

	function replaceMaterial(object: Object3D) {
		// let evenOdd = false;

		// console.log("Replace Material ");

		object.traverse(async (child) => {
			// Setze
			child.layers.set(0);

			// Neue Nationalgalerie ==> Planes
			if (Object.prototype.hasOwnProperty.call(child.userData, "nngMaterials")) {
				// console.log("=> subject to change");
				const material = new MeshBasicMaterial({ color: 0xffffff, alphaTest: 0.5, side: DoubleSide, map: ((child as Mesh).material as MeshStandardMaterial).map });
				(child as Mesh).material = material;
			}

			if (child instanceof Mesh && child.material instanceof Material) {
				//temporärer Fix für NN
				if (page && page.model && page.model.name.includes("neuenationalgallerie")) {
				}

				const matName = child.material.name;

				// console.log("Materialname:", matName);
				if (matName === "bismuth") {
					const bizmuthMap = (child.material as MeshStandardMaterial).map;
					const bizmuthMetalnessMap = (child.material as MeshStandardMaterial).metalnessMap;
					const bizmuthRoughnessMap = (child.material as MeshStandardMaterial).roughnessMap;
					child.material = new MeshStandardMaterial({ map: bizmuthMap, metalnessMap: bizmuthMetalnessMap, roughnessMap: bizmuthRoughnessMap });
				}

				if (matName === "bismuth_vines") {
					const bizmuthVinesMap = child.material.map;
					child.material = new MeshStandardMaterial({ map: bizmuthVinesMap, side: DoubleSide, transparent: false, alphaTest: 0.35 });
				}
				if (matName === "Eco_Nodes" || matName === "Eco_Lines") {
					child.layers.set(2);
					child.frustumCulled = false;
				}
				if (matName === "Earth_m") {
					// //FU Object
					// if (child.material.name === "CellMaterial") {
					// 	console.log("CellMaterial", child.material);
					// }

					// if (child.material.name === "Black") {
					// 	child.layers.set(1);
					// }

					(child.material as MeshStandardMaterial).normalScale.set(0.5, -0.5);
				}

				if (Object.keys(MaterialLibrary).includes(matName)) {
					const material = await MaterialLibrary[matName](); //getMaterial(matName);
					// console.log("=> Replaced Material ", matName);

					//Temporärer Fix für Octapatch
					// if (matName === "Dispersion") {
					// 	matName += evenOdd ? "_1" : "";
					// 	evenOdd = !evenOdd;

					// 	// console.log("=========> Change Dispersion", evenOdd, matName);
					// }

					// FU
					if (matName === "bismuth_vines") {
						child.material.side = DoubleSide;
						child.material.depthTest = false;
						child.material.transparent = false;
					}

					//
					if (matName === "Eco_Lines") {
						child.material.side = DoubleSide;
						child.material.depthTest = false;
						child.material.depthWrite = false;
						child.material.transparent = false;
					}

					// if (matName === "blackmarlin_body" || matName === "blackmarlin_fins.001") {
					// 	// console.log("blackmarlin_body", child.material);

					// 	(material as MeshStandardMaterial).map = child.material.map;
					// 	(material as MeshStandardMaterial).metalnessMap = child.material.metalnessMap;
					// 	(material as MeshStandardMaterial).roughnessMap = child.material.roughnessMap;
					// 	(material as MeshStandardMaterial).emissiveMap = child.material.emissiveMap;
					// }

					// // Hold den eigentlichen Namen

					// if (matName === "soap_trans") {
					// 	child.material = new MeshBasicMaterial({ color: new Color("#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0")) });
					// }

					if (matName === "Coffee_Glas") {
						(material as MeshPhysicalMaterial).transmissionMap = child.material.roughnessMap;
						(material as MeshPhysicalMaterial).roughnessMap = child.material.roughnessMap;
						(material as MeshPhysicalMaterial).roughness = 1;
						child.renderOrder = 2;
					}
					if (matName === "Kaffee") {
						child.renderOrder = 1;
					}

					child.material = material;

					// // Falls die Materialeigenschaften animiert werden müssen
					// //Beispiel: Octapatch
					if (material.userData.animated) {
						child.material.userData.mesh = child;

						animatedMaterials.current.push(child.material);
					}

					if (material.userData.always_animated) {
						child.material.userData.mesh = child;

						alwaysanimatedMaterials.current.push(child.material);
					}
				}
			}
		});
	}

	useFrame((_, delta) => {
		mixer.current?.update(delta);

		alwaysanimatedMaterials.current.map((m) =>
			m.userData.always_animated({
				clock: clock,
				gl: gl,
				camera: camera,
				scene: realScene,
				mesh: m.userData.mesh,
			})
		);

		// console.log("frame", masterAnimation);
		if (masterAnimation && masterAnimation?.isRunning()) {
			setPaused(false);
			setAnimationTime(masterAnimation?.time || 0);

			if (loopIteration === 1 && masterAnimation.time >= freezeTime) {
				setIsAnimationPlaying(false);

				setTime(freezeTime);

				onAnimationDone();

				setLoopIteration(0);
			}

			// console.log(masterAnimation.time / masterAnimation.getClip().duration);
			animatedMaterials.current.map((m) =>
				m.userData.animated({
					animationTime: masterAnimation.time / masterAnimation.getClip().duration,
					clock: clock,
					gl: gl,
					camera: camera,
					scene: realScene,
					mesh: m.userData.mesh,
				})
			);
		} else {
			setPaused(true);
		}
	});

	if (!isLoaded) {
		return null;
	}

	// console.log(" => ", page.model);

	return (
		<>
			<FloatAnimation page={page} isAnimating={!paused}>
				<primitive object={scene} scale={1} dispose={null} />

				{page && page.model && page.model.name.includes("tempelhof") && (
					<>
						<ambientLight intensity={1} />
					</>
				)}

				{page && page.model && page.model.name.includes("coffee_partnership") && (
					<>
						<ambientLight intensity={1} />
						<directionalLight intensity={1.5} />
					</>
				)}
				{/* {page && page.model && page.model.name.includes("tempelhof") && <Particles animationTime={animationTime} animationIsPlaying={!paused} />} */}

				<WaterScene page={page} animationIsPlaying={!paused} animationTime={animationTime} />
				{/* <HumanoidScene animationIsPlaying={!paused} animationTime={animationTime} page={page} /> */}

				<Preload all />
			</FloatAnimation>
		</>
	);
});

Model3D.displayName = "Model3D";

export default Model3D;
